import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RamonVideoClip } from "./types";
import { markGemaTiming } from "./gemaTiming";

type Slot = { clip: RamonVideoClip; token: number; compatibility: string };
type Pending = { index: 0 | 1; token: number; requestKey: string; playbackId?: number; source?: "home" | "onboarding" | "personalizar"; action?: string };
type Speculative = { index: 0 | 1; token: number };

const clipKey = (clip: RamonVideoClip) => `${clip.mov}|${clip.webm}`;

interface Options {
  clip: RamonVideoClip;
  requestKey: string;
  compatibility: string;
  preloadClip?: RamonVideoClip;
  enabled: boolean;
  onEnded: () => void;
  onPromoted?: (requestKey: string) => void;
  playbackId?: number;
  timingSource?: "home" | "onboarding" | "personalizar";
  action?: string;
}

interface VideoConnection extends Navigator {
  connection?: { saveData?: boolean; effectiveType?: string };
}

/** Keeps the last decoded frame visible until the replacement has a decoded frame of its own. */
export function useVideoHandoff(options: Options) {
  const firstRef = useRef<HTMLVideoElement>(null);
  const secondRef = useRef<HTMLVideoElement>(null);
  const refs = useMemo(() => [firstRef, secondRef] as const, []);
  const token = useRef(0);
  const activeRequest = useRef(options.requestKey);
  const pendingRef = useRef<Pending | null>(null);
  const speculativeRef = useRef<Speculative | null>(null);
  const visibleRequest = useRef<string | null>(null);
  const onEndedRef = useRef(options.onEnded);
  const onPromotedRef = useRef(options.onPromoted);
  onEndedRef.current = options.onEnded;
  onPromotedRef.current = options.onPromoted;
  const [slots, setSlots] = useState<[Slot, Slot]>(() => [
    { clip: options.clip, token: 0, compatibility: options.compatibility },
    { clip: options.clip, token: -1, compatibility: options.compatibility },
  ]);
  const [pending, setPending] = useState<Pending | null>(null);
  const [visible, setVisible] = useState<0 | 1 | null>(null);
  const visibleRef = useRef<0 | 1 | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!options.enabled) return;
    activeRequest.current = options.requestKey;
    setFailed(false);
    speculativeRef.current = null;
    const currentVisible = visibleRef.current;
    if (currentVisible !== null) refs[currentVisible].current?.pause();
    const nextToken = ++token.current;
    const target: 0 | 1 = visibleRef.current === null ? (nextToken % 2 === 1 ? 0 : 1) : visibleRef.current === 0 ? 1 : 0;
    const next: Pending = { index: target, token: nextToken, requestKey: options.requestKey, playbackId: options.playbackId, source: options.timingSource, action: options.action };
    pendingRef.current = next;
    setSlots(current => {
      const copy = [...current] as [Slot, Slot];
      copy[target] = { clip: options.clip, token: nextToken, compatibility: options.compatibility };
      return copy;
    });
    setPending(next);
  }, [options.action, options.clip, options.compatibility, options.enabled, options.playbackId, options.requestKey, options.timingSource, refs]);

  useEffect(() => {
    if (!pending || !options.enabled) return;
    const element = refs[pending.index].current;
    if (!element) return;
    let frame = 0;
    let cancelled = false;
    const isCurrent = () => !cancelled && pendingRef.current?.token === pending.token && activeRequest.current === pending.requestKey;
    const promote = () => {
      if (!isCurrent()) return;
      const previous = visibleRef.current;
      pendingRef.current = null;
      setPending(null);
      visibleRef.current = pending.index;
      visibleRequest.current = pending.requestKey;
      setVisible(pending.index);
      setFailed(false);
      if (previous !== null && previous !== pending.index) refs[previous].current?.pause();
      onPromotedRef.current?.(pending.requestKey);
      markGemaTiming("first_frame", { playbackId: pending.playbackId, source: pending.source, action: pending.action, prepared: alreadyPrepared });
    };
    const firstFrame = () => {
      if (!isCurrent()) return;
      if (typeof element.requestVideoFrameCallback === "function") {
        frame = element.requestVideoFrameCallback(() => promote());
      } else if (element.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        frame = requestAnimationFrame(promote);
      }
    };
    element.addEventListener("playing", firstFrame);
    const timeout = window.setTimeout(() => {
      if (!isCurrent()) return;
      pendingRef.current = null;
      setPending(null);
      setFailed(true);
    }, 2500);
    const requestedSlot = slots[pending.index];
    const selectedSource = element.currentSrc;
    const alreadyPrepared = element.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
      (selectedSource === new URL(requestedSlot.clip.mov, window.location.href).href ||
       selectedSource === new URL(requestedSlot.clip.webm, window.location.href).href);
    markGemaTiming("clip_requested", { playbackId: pending.playbackId, source: pending.source, action: pending.action, prepared: alreadyPrepared });
    if (element.ended || element.currentTime > 0) element.currentTime = 0;
    if (!alreadyPrepared) element.load();
    void element.play().catch((error: DOMException) => {
      if (isCurrent() && error.name !== "AbortError") {
        pendingRef.current = null;
        setPending(null);
        setFailed(true);
      }
    });
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
      element.removeEventListener("playing", firstFrame);
      if (frame) {
        if (typeof element.cancelVideoFrameCallback === "function") element.cancelVideoFrameCallback(frame);
        else cancelAnimationFrame(frame);
      }
    };
  }, [options.enabled, pending, refs, slots]);

  useEffect(() => {
    if (!options.enabled || visible === null || pending || pendingRef.current || !options.preloadClip) return;
    const connection = (navigator as VideoConnection).connection;
    if (connection?.saveData || connection?.effectiveType === "slow-2g" || connection?.effectiveType === "2g") return;
    const hidden: 0 | 1 = visible === 0 ? 1 : 0;
    if (clipKey(slots[hidden].clip) === clipKey(options.preloadClip)) return;
    const preloadToken = ++token.current;
    speculativeRef.current = { index: hidden, token: preloadToken };
    setSlots(current => {
      const copy = [...current] as [Slot, Slot];
      copy[hidden] = { clip: options.preloadClip!, token: preloadToken, compatibility: options.compatibility };
      return copy;
    });
  }, [options.compatibility, options.enabled, options.preloadClip, pending, refs, slots, visible]);

  useEffect(() => {
    const speculative = speculativeRef.current;
    if (!speculative || slots[speculative.index].token !== speculative.token) return;
    speculativeRef.current = null;
    refs[speculative.index].current?.load();
  }, [refs, slots]);

  const mediaFailed = useCallback((index: 0 | 1) => {
    const preparing = pendingRef.current;
    if (!preparing || preparing.index !== index) return;
    pendingRef.current = null;
    setPending(null);
    setFailed(true);
  }, []);
  const mediaEnded = useCallback((index: 0 | 1) => {
    if (visibleRef.current !== index || visibleRequest.current !== activeRequest.current) return;
    onEndedRef.current();
  }, []);

  const visibleCompatible = visible !== null && slots[visible].compatibility === options.compatibility;
  return { refs, slots, visible, visibleCompatible, preparing: pending !== null, failed, mediaFailed, mediaEnded };
}
